import { memo, useState, useEffect, useRef } from "react";

interface SectionDividerProps {
  className?: string;
}

// Check if user prefers reduced motion
const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const SectionDivider = memo(({ className = "" }: SectionDividerProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const dividerRef = useRef<HTMLDivElement>(null);

  // Listen for reduced motion preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = () => setReducedMotion(mediaQuery.matches);
    
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    // If reduced motion, show immediately
    if (reducedMotion) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5, rootMargin: "0px" }
    );

    if (dividerRef.current) {
      observer.observe(dividerRef.current);
    }

    return () => observer.disconnect();
  }, [reducedMotion]);

  // Reduced motion: show static divider without animations
  if (reducedMotion) {
    return (
      <div 
        ref={dividerRef}
        className={`flex items-center justify-center py-4 md:py-6 ${className}`} 
        aria-hidden="true"
      >
        <div className="h-px w-16 md:w-24 bg-gradient-to-r from-transparent via-border to-transparent" />
        <div 
          className="mx-3 md:mx-4 w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-primary/40"
          style={{ boxShadow: "0 0 8px hsl(var(--primary) / 0.3)" }}
        />
        <div className="h-px w-16 md:w-24 bg-gradient-to-l from-transparent via-border to-transparent" />
      </div>
    );
  }

  return (
    <div 
      ref={dividerRef}
      className={`flex items-center justify-center py-4 md:py-6 ${className}`} 
      aria-hidden="true"
    >
      <div 
        className={`h-px w-16 md:w-24 bg-gradient-to-r from-transparent via-border to-transparent transition-all duration-700 ease-out ${
          isVisible ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"
        }`}
        style={{ 
          transformOrigin: "right",
          willChange: isVisible ? "auto" : "opacity, transform"
        }}
      />
      <div 
        className={`mx-3 md:mx-4 w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-primary/30 transition-all duration-500 ease-out ${
          isVisible ? "opacity-100 scale-100" : "opacity-0 scale-0"
        }`}
        style={{ 
          transitionDelay: "300ms",
          boxShadow: isVisible ? "0 0 6px hsl(var(--primary) / 0.2)" : "none",
          willChange: isVisible ? "auto" : "opacity, transform",
          animation: isVisible ? "pulse 6s ease-in-out infinite 1s" : "none"
        }}
      />
      <div 
        className={`h-px w-16 md:w-24 bg-gradient-to-l from-transparent via-border to-transparent transition-all duration-700 ease-out ${
          isVisible ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"
        }`}
        style={{ 
          transformOrigin: "left", 
          transitionDelay: "100ms",
          willChange: isVisible ? "auto" : "opacity, transform"
        }}
      />
    </div>
  );
});

SectionDivider.displayName = 'SectionDivider';

export default SectionDivider;